import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCommissionAdvance, useUpdateCommissionAdvance } from "../../../../../common/api/queries";
import { ModalSizes } from "../../../../../common/constants";
import type { InvestmentContract } from "../../../../contract/types";
import { CommissionAdvance, CreateCommissionAdvance, UpdateCommissionAdvance } from "../../types";

interface Props {
  contract: InvestmentContract;
  advance?: CommissionAdvance;
  isOpen: boolean;
  onCancel: VoidFunction;
}

export const CommissionAdvanceModalForm = ({ contract, advance, isOpen, onCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateCommissionAdvance & UpdateCommissionAdvance>();

  const createCommissionAdvance = useCreateCommissionAdvance();
  const updateCommissionAdvance = useUpdateCommissionAdvance();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        contractOptimisticLockVersion: contract.optimisticLockVersion,
        optimisticLockVersion: advance?.optimisticLockVersion,
        dueFeePaymentsCount: advance?.dueFeePaymentsCount ?? 0,
        paidFeePaymentsCount: advance?.paidFeePaymentsCount ?? 0,
        surplusAmount: advance?.surplusAmount ?? 0,
      });
    }
  }, [advance, contract.optimisticLockVersion, form, isOpen]);

  // Once the counters reach the fee payments count, the advance is finished - the API accepts only lower values
  const maxFeePaymentsCount = (advance?.feePaymentsCount ?? contract.feePaymentsCount) - 1;

  const handleFormSubmit = () => {
    form.validateFields().then(({ contractOptimisticLockVersion, optimisticLockVersion, ...counters }) => {
      if (advance) {
        updateCommissionAdvance.mutate(
          { id: contract.id, object: { ...counters, optimisticLockVersion } },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.advance.attrs", error.violations),
          },
        );
      } else {
        createCommissionAdvance.mutate(
          { id: contract.id, object: { ...counters, contractOptimisticLockVersion } },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.advance.attrs", error.violations),
          },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      title={advance ? t("commissions.advance.titles.update") : t("commissions.advance.titles.create")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionAdvance.isPending || updateCommissionAdvance.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionAdvanceForm">
        <HiddenInput name="contractOptimisticLockVersion" />
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="dueFeePaymentsCount"
          label={t("commissions.advance.attrs.dueFeePaymentsCount")}
          rules={[validations.notNull, validations.minNumber(0), validations.maxNumber(maxFeePaymentsCount)]}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item
          name="paidFeePaymentsCount"
          label={t("commissions.advance.attrs.paidFeePaymentsCount")}
          rules={[validations.notNull, validations.minNumber(0), validations.maxNumber(maxFeePaymentsCount)]}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item
          name="surplusAmount"
          label={t("commissions.advance.attrs.surplusAmount")}
          rules={[validations.notNull, validations.minNumber(0)]}
        >
          <InputNumber type="decimal" addon="euro" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
